import { RadioGroup, RadioGroupItem } from "design-system/atoms/RadioGroup"
import { FormControl, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { GetWidgetConfigurationResponseData } from "schema"
import z from "zod"
import { WidgetConfigurationFormType } from ".."

type SendBehaviorProps = {
  form: UseFormReturn<WidgetConfigurationFormType>
}

type SendBehaviorEnum = z.infer<typeof GetWidgetConfigurationResponseData>["sendBehavior"]

export const SendBehavior = ({ form }: SendBehaviorProps) => {
  const { t } = useTranslation()

  const options = [
    {
      label: t("campaigns.templates.widget.config.appearance.sendBehavior.options.clickToSend"),
      description: t(
        "campaigns.templates.widget.config.appearance.sendBehavior.descriptions.clickToSend",
      ),
      value: "clickToSend",
    },
    {
      label: t("campaigns.templates.widget.config.appearance.sendBehavior.options.enterToSend"),
      description: t(
        "campaigns.templates.widget.config.appearance.sendBehavior.descriptions.enterToSend",
      ),
      value: "enterToSend",
    },
  ]

  return (
    <FormField
      control={form.control}
      name="sendBehavior"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <RadioGroup
              value={field.value}
              onValueChange={(value) => form.setValue(field.name, value as SendBehaviorEnum)}
              className="grid grid-cols-2 gap-3"
            >
              {options.map((option) => (
                <label
                  key={option.value}
                  htmlFor={`sendBehavior-${option.value}`}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors",
                    field.value === option.value
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/20 hover:border-muted-foreground/40",
                  )}
                >
                  <RadioGroupItem id={`sendBehavior-${option.value}`} value={option.value} />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-base font-medium leading-snug">{option.label}</span>
                    <span className="text-sm text-muted-foreground">{option.description}</span>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
